import React from 'react';
import Navbar from '../components/Navbar';
import CalendarGrid from '../components/CalendarGrid';
import { useToast } from '../context/ToastContext';
import { useCalendar } from '../context/CalendarContext';

/**
 * Simple Calendar Page
 * Displays courses added from the Courses page on a weekly calendar grid
 */
function CalendarPage() {
  const { showToast } = useToast();
  const { plannedCourses, loading, removeCourse } = useCalendar();
  const [searchQuery, setSearchQuery] = React.useState('');

  const totalUnits = plannedCourses.reduce((sum, c) => sum + (parseInt(c.units) || 4), 0);

  // Filter courses by search
  const filteredCourses = plannedCourses.filter(course => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      course.class_tag?.toLowerCase().includes(query) ||
      course.class_name?.toLowerCase().includes(query)
    );
  });

  // Remove course handler
  const handleRemoveCourse = async (course) => {
    const result = await removeCourse(course.class_tag);
    if (result.success) {
      showToast(`Removed ${course.class_tag}`, 'success');
    } else {
      showToast('Failed to remove course', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-dark-100 dark:via-dark-200 dark:to-dark-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-600 dark:text-dark-600">Loading calendar...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-dark-100 dark:via-dark-200 dark:to-dark-100">
      <Navbar />

      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-800">
              Course Calendar
            </h1>
            <p className="text-sm text-gray-600 dark:text-dark-600 mt-1">
              Spring 2026 • {plannedCourses.length} course{plannedCourses.length !== 1 ? 's' : ''} • {totalUnits} units
            </p>
            <p className="text-xs text-gray-500 dark:text-dark-500 mt-2">
              Add courses from the Courses page to build your schedule
            </p>
          </div>

          {/* Calendar Grid */}
          <CalendarGrid
            courses={plannedCourses}
            onRemoveCourse={handleRemoveCourse}
          />

          {/* Course List at Bottom */}
          {plannedCourses.length > 0 && (
            <div className="mt-6 bg-white dark:bg-dark-200 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-800">
                  Added Courses ({plannedCourses.length})
                </h2>

                {/* Search bar */}
                <div className="relative w-64">
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pr-10 bg-gray-50 dark:bg-dark-300 border border-gray-300 dark:border-dark-400 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  />
                  <svg className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Course list */}
              <div className="space-y-2">
                {filteredCourses.map(course => (
                  <div
                    key={course.class_tag}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-300 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-400 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-dark-800">
                        {course.class_tag}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-dark-600 mt-0.5">
                        {course.class_name}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-dark-500">
                        {course.mean_hours && (
                          <span>{parseFloat(course.mean_hours).toFixed(1)} hrs/wk</span>
                        )}
                        {course.overall_score_course_mean && (
                          <span>★ {parseFloat(course.overall_score_course_mean).toFixed(1)}</span>
                        )}
                        <span>{course.units || 4} units</span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleRemoveCourse(course)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Remove from calendar"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}

                {filteredCourses.length === 0 && searchQuery && (
                  <div className="text-center py-8 text-gray-500 dark:text-dark-600">
                    No courses match "{searchQuery}"
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CalendarPage;